import React, { useState } from 'react'; 
import { 
	Box, 
	Button, 
	FormControl, 
	FormControlLabel, 
	FormLabel, 
	Radio, 
	RadioGroup, 
	TextField, 
	Typography 
} from '@mui/material'; 
import RichTextEmailEditor from 'utils/RichTextEmailEditor'; 
import { useTheme } from '@mui/material/styles';

const ChannelModal = ({ channelOptions, handleClose, onAddChannel, selectedChannel }: any) => {
	const theme = useTheme();
	const colors = theme.palette;

	let initialChannelType = '';

	if (selectedChannel) {
		initialChannelType = selectedChannel.channel;
	} else if (channelOptions.length > 0) {
		initialChannelType = channelOptions[0];
	}

	const [channelType, setChannelType] = useState(initialChannelType);

	const [channelConfig, setChannelConfig] = useState<any>(selectedChannel ?? {});
	const [errorText, setErrorText] = useState<string|null>(null);

	const handleChange = (event: any) => {
		setChannelType(event.target.value);
		setChannelConfig({});
	};

    const checkMandatoryFields = () => {
        if((channelType === 'Email' && (!channelConfig.templateName || !channelConfig.emailSubject || !channelConfig.emailRecipients || !channelConfig.htmlTemplate)) || 
        (channelType === 'Service Now' && (!channelConfig.templateName || !channelConfig.payload )) ||
        (channelType === 'Teams' && (!channelConfig.templateName || !channelConfig.title || !channelConfig.bodyTemplate || !channelConfig.teamsMail))){
            setErrorText('Please fill up all required fields.')
            return false
        }
        return true
    }

	const handleSaveChannel = () => {
        const isValid = checkMandatoryFields()
        if(!isValid) return
		if (channelType === 'Email') {
			const emailRecipientsString = typeof channelConfig.emailRecipients === 'string' 
				? channelConfig.emailRecipients 
				: "";
	
			const emailRecipientsArray = Array.isArray(channelConfig.emailRecipients) ? channelConfig.emailRecipients : 
				emailRecipientsString.split(',').map((email: string) => email.trim()).filter((email: string) => email.length > 0);
			onAddChannel(channelType, { ...channelConfig, emailRecipients: emailRecipientsArray, isEnabled: true });
		} else {
			onAddChannel(channelType, { ...channelConfig, isEnabled: true });
		}
	};

	const handleSourceConfigChange = ({ name, value }: any) => {
        setErrorText(null)
		setChannelConfig({ ...channelConfig, [name]: value });
	};

	const inputSx = {
		'& .MuiOutlinedInput-root': {
			backgroundColor: `${colors.inputNew.inputBackground} !important`,
			color: `${colors.inputNew.inputText} !important`,

			'& fieldset': {
				borderColor: `${colors.inputNew.inputBorder} !important`,
			},

			'&:hover fieldset': {
				borderColor: `${colors.inputNew.inputBorderHover} !important`,
			},

			'&.Mui-focused fieldset': {
				borderColor: `${colors.inputNew.inputBorderFocus} !important`,
			},

			'& input, & textarea': {
				color: `${colors.inputNew.inputText} !important`,
				WebkitTextFillColor: `${colors.inputNew.inputText} !important`,

				'&::placeholder': {
					color: `${colors.inputNew.inputPlaceholder} !important`,
					WebkitTextFillColor: `${colors.inputNew.inputPlaceholder} !important`,
					opacity: 1,
				},
			},
		},

		'& .MuiInputBase-input': {
			color: `${colors.inputNew.inputText} !important`,
			WebkitTextFillColor: `${colors.inputNew.inputText} !important`,
		},

		'& .MuiInputLabel-root': {
			color: `${colors.inputNew.inputPlaceholder} !important`,
		},

		'& .MuiInputLabel-root.Mui-focused': {
			color: `${colors.inputNew.inputPlaceholder} !important`,
		},
	};

	const radioSx = {
		padding: '6px',

		'& .MuiSvgIcon-root': {
			color: `${colors.radioGroup.radioBorder} !important`,
		},

		'&.Mui-checked .MuiSvgIcon-root': {
			color: `${colors.radioGroup.radioCheckedBackground} !important`,
		},

		'&.Mui-disabled .MuiSvgIcon-root': {
			color: `${colors.radioGroup.disabledBorder} !important`,
		},
	};

	const radioLabelSx = {
		'& .MuiFormControlLabel-label': {
			color: `${colors.radioGroup.optionText} !important`,
			fontSize: '14px',
      		fontWeight: '500 !important',
		},

		'& .MuiFormControlLabel-label.Mui-disabled': {
			color: `${colors.radioGroup.disabledBorder} !important`,
		},
	};

	let channelSpecificContent: React.ReactNode;

  if (channelType === 'Email') {
    channelSpecificContent = (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row'
            },
            gap: 2,
            width: '100%'
          }}
        >
          <FormControl
            sx={{
              width: {
                xs: '100%',
                sm: '42%'
              }
            }}
          >
            <TextField
              label="Template name*"
              variant="outlined"
              size="small"
              value={channelConfig.templateName}
              onChange={(e) =>
                handleSourceConfigChange({ name: 'templateName', value: e.target.value })
              }
              sx={{
                width: '100%',
                ...inputSx,
              }}
            />
          </FormControl>

          <FormControl
            sx={{
              width: {
                xs: '100%',
                sm: '58%'
              }
            }}
          >
            <TextField
              label="Email Subject*"
              variant="outlined"
              size="small"
              value={channelConfig.emailSubject}
              onChange={(e) =>
                handleSourceConfigChange({ name: 'emailSubject', value: e.target.value })
              }
              sx={{
                width: '100%',
                ...inputSx,
              }}
            />
          </FormControl>
        </Box>

        <FormControl required fullWidth>
          <FormLabel
            sx={{
              color: `${colors.inputNew.labelText} !important`,
              '&.Mui-focused': {
                color: `${colors.inputNew.labelText} !important`,
              },
              textAlign: 'left'
            }}
          >
            Email Body
          </FormLabel>
          <TextField
            fullWidth
            multiline
            rows={5}
            variant="outlined"
            value={channelConfig.htmlTemplate}
            placeholder="Email <body>"
            onChange={(e) =>
              handleSourceConfigChange({ name: 'htmlTemplate', value: e.target.value })
            }
            sx={{
              ...inputSx,
              width: '100%',
              marginTop: 2,
            }}
          />
        </FormControl>

        <FormControl fullWidth>
          <TextField
            label="Select Recipient Emails or DL*"
            variant="outlined"
            size="small"
            value={channelConfig.emailRecipients}
            onChange={(e) =>
              handleSourceConfigChange({ name: 'emailRecipients', value: e.target.value })
            }
            sx={{
              width: '100%',
              marginTop: 2,
              ...inputSx,
            }}
          />
        </FormControl>
      </>
    );
  } else if (channelType === 'Service Now') {
    channelSpecificContent = (
      <>
        <FormControl
          sx={{
            width: {
              xs: '100%',
              sm: 300
            }
          }}
        >
          <TextField
            label="Template name*"
            variant="outlined"
            size="small"
            value={channelConfig.templateName}
            onChange={(e) =>
              handleSourceConfigChange({ name: 'templateName', value: e.target.value })
            }
            sx={{
              width: '100%',
              ...inputSx,
            }}
          />
        </FormControl>

        <FormControl required fullWidth>
          <FormLabel
            sx={{
              color: `${colors.inputNew.labelText} !important`,
              '&.Mui-focused': {
                color: `${colors.inputNew.labelText} !important`,
              },
              textAlign: 'left'
            }}
          >
            Payload
          </FormLabel>
          <TextField
            fullWidth
            multiline
            rows={5}
            variant="outlined"
            placeholder="Payload"
            value={channelConfig.payload}
            onChange={(e) =>
              handleSourceConfigChange({ name: 'payload', value: e.target.value })
            }
            sx={{
              ...inputSx,
              width: '100%',
              marginTop: 2,
            }}
          />
        </FormControl>

        <FormControl fullWidth>
          <TextField
            label="Service Now URL"
            variant="outlined"
            size="small"
            value={channelConfig.serviceNowUrl}
            onChange={(e) =>
              handleSourceConfigChange({ name: 'serviceNowUrl', value: e.target.value })
            }
            sx={{
              width: '100%',
              marginTop: 2,
              ...inputSx,
            }}
          />
        </FormControl>
      </>
    );
  } else {
    channelSpecificContent = (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row'
            },
            alignItems: 'center',
            gap: 2,
            width: '100%'
          }}
        >
          <FormControl
            sx={{
              width: {
                xs: '100%',
                sm: '50%'
              }
            }}
          >
            <TextField
              label="Template name*"
              variant="outlined"
              size="small"
              value={channelConfig.templateName}
              onChange={(e) =>
                handleSourceConfigChange({ name: 'templateName', value: e.target.value })
              }
              sx={{
                width: '100%',
                ...inputSx,
              }}
            />
          </FormControl>

          <FormControl
            sx={{
              width: {
                xs: '100%',
                sm: '50%'
              }
            }}
          >
            <TextField
              label="Title*"
              variant="outlined"
              size="small"
              value={channelConfig.title}
              onChange={(e) =>
                handleSourceConfigChange({ name: 'title', value: e.target.value })
              }
              sx={{
                width: '100%',
                ...inputSx,
              }}
            />
          </FormControl>
        </Box>

        <FormControl required fullWidth>
          <FormLabel
            sx={{
              color: `${colors.inputNew.labelText} !important`,
              '&.Mui-focused': {
                color: `${colors.inputNew.labelText} !important`,
              },
              textAlign: 'left'
            }}
          >
            Message Body
          </FormLabel>
          <RichTextEmailEditor
            value={channelConfig.bodyTemplate}
            onChange={(value) =>
              handleSourceConfigChange({ name: 'bodyTemplate', value })
            }
          />
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <FormControl sx={{ width: '100%' }}>
            <TextField
              label="Teams Mail*"
              variant="outlined"
              size="small"
              value={channelConfig.teamsMail}
              onChange={(e) =>
                handleSourceConfigChange({ name: 'teamsMail', value: e.target.value })
              }
              sx={{
                width: '100%',
                marginTop: 2,
                ...inputSx,
              }}
            />
          </FormControl>
        </Box>
      </>
    );
  }

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 24,
				padding: '24px',
				width: '100%',
				backgroundColor: colors.modal.bg,
				color: colors.inputNew.inputText,
				boxSizing: 'border-box'
			}}
		>
			<FormControl fullWidth required>
				<FormLabel
					sx={{
						color: `${colors.inputNew.inputPlaceholder} !important`,
						'&.Mui-focused': {
							color: `${colors.inputNew.inputPlaceholder} !important`
						},
						textAlign: 'left'
					}}
				>
					Select Channel
				</FormLabel>

				<RadioGroup
					row
					name="channelType"
					value={channelType}
					onChange={handleChange}
					sx={{
						mt: 1,
						gap: 2,
						flexWrap: 'wrap'
					}}
				>
					{selectedChannel ? (
						<FormControlLabel
							value={selectedChannel.channel}
							control={
								<Radio
									variant="pepwiseRadio"
									sx={radioSx}
								/>
							}
							label={selectedChannel.channel}
							sx={radioLabelSx}
						/>
					) : (
						channelOptions.map((channel: string) => (
							<FormControlLabel
								key={channel}
								value={channel}
								control={
									<Radio
										variant="pepwiseRadio"
										sx={radioSx}
									/>
								}
								label={channel}
								sx={radioLabelSx}
							/>
						))
					)}
				</RadioGroup>
			</FormControl>

			{channelSpecificContent}

			<Box
				sx={{
					display: 'flex',
					flexDirection: {
						xs: 'column',
						sm: 'row'
					},
					justifyContent: 'space-between',
					mt: 2
				}}
			>
                <Typography
					style={{
						color: colors.inputNew.error,
						cursor: 'pointer',
						marginTop: '6px',
						marginRight: '25px'
					}}					
				>
					{errorText ? `*${errorText}` : ''}
				</Typography>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button 
                      variant='pepwiseButtonText'
                      style={{ 
                        color: colors.buttons.primary.color,
                        cursor: 'pointer', 
                        marginTop: '6px', 
                        marginRight: '25px' 
                      }} 
                      onClick={handleClose} 
                    > 
                      CANCEL
                    </Button>

                    <Button
                      variant="pepwiseSecondary"
                      onClick={handleSaveChannel}
                      sx={{
                        backgroundColor: `${colors.buttons.primary.solidBg} !important`,
                        color: `${colors.buttons.primary.solidText} !important`,

                        '&:hover': {
                          backgroundColor: `${colors.buttons.primary.bgColor} !important`,
                          color: `${colors.buttons.primary.solidText} !important`,
                        },
                      }}
                    >
                        SAVE
                    </Button>
                </Box>
			</Box>
		</div>
	);
};

export default ChannelModal;